import { useEffect, useMemo, useState, type ReactNode } from 'react'
import { Icon } from './icons'

/* -------------------------------------------------------------------------- */
/* 分页                                                                        */
/* -------------------------------------------------------------------------- */

/**
 * 客户端分页状态机。
 *
 * 项目里此前**一个分页实现都没有** —— 后端契约早有 `page / perPage`，前端从来没用过，
 * 于是列表要么全量铺开（任务队列），要么硬截断（「本页历史」原来 `slice(0, 4)`），
 * 要么靠 `perPage: 300` 一次拉满（资产库）。全量渲染在数据长起来之后就是卡顿，
 * 硬截断则是**数据还在、但用户看不见**。
 *
 * 三条边界必须处理，少一条就会出「空白页」：
 * 1. `resetKey` 变化（切筛选 / 切页签）→ 回第 1 页。
 *    不回的话用户停在第 5 页、筛选后总量只剩 1 页，看到的是**空白**，会以为筛选坏了。
 * 2. `pageCount` 变小（删了一条 / 筛选后变少）→ 把越界的 `page` 夹回末页。
 * 3. `pageSize` 变化 → 回第 1 页（否则行号会莫名跳掉）。
 */
export function usePagination<T>(
  items: T[],
  { pageSize: initialSize = 10, resetKey }: { pageSize?: number; resetKey?: unknown } = {},
) {
  const [pageSize, setPageSize] = useState(initialSize)
  const [page, setPage] = useState(1)

  const total = items.length
  const pageCount = Math.max(1, Math.ceil(total / pageSize))

  // 边界 1 + 3：筛选条件或每页条数变了，回到第 1 页
  useEffect(() => {
    setPage(1)
  }, [resetKey, pageSize])

  // 边界 2：总量缩水后当前页可能已越界，夹回末页
  useEffect(() => {
    setPage((p) => (p > pageCount ? pageCount : p))
  }, [pageCount])

  const start = (page - 1) * pageSize
  const pageItems = useMemo(
    () => items.slice(start, start + pageSize),
    [items, start, pageSize],
  )

  return {
    page,
    setPage,
    pageSize,
    setPageSize,
    pageCount,
    total,
    pageItems,
    /** 当前页在全集里的序号区间，供「第 3-12 条 / 共 46 条」展示 */
    from: total ? start + 1 : 0,
    to: Math.min(start + pageSize, total),
  }
}

/**
 * 页码窗口：最多 7 个槽位，超出用省略号。
 * 不做窗口化的话，100 页就是 100 个按钮，把工具栏挤成一坨。
 */
function pageWindow(page: number, pageCount: number): (number | '…')[] {
  if (pageCount <= 7) return Array.from({ length: pageCount }, (_, i) => i + 1)
  if (page <= 4) return [1, 2, 3, 4, 5, '…', pageCount]
  if (page >= pageCount - 3)
    return [1, '…', pageCount - 4, pageCount - 3, pageCount - 2, pageCount - 1, pageCount]
  return [1, '…', page - 1, page, page + 1, '…', pageCount]
}

export function Pager({
  page,
  pageCount,
  total,
  from,
  to,
  pageSize,
  onPage,
  onPageSize,
  pageSizes = [10, 20, 50],
  unit = '条',
}: {
  page: number
  pageCount: number
  total: number
  from: number
  to: number
  pageSize: number
  onPage: (n: number) => void
  /** 不传则不显示「每页 N 条」选择器 */
  onPageSize?: (n: number) => void
  pageSizes?: number[]
  unit?: string
}) {
  if (!total) return null

  const multi = pageCount > 1

  return (
    <div className="pager">
      <span className="pager__info">
        共 <b>{total}</b> {unit}
        {multi ? ` · 第 ${from}-${to} ${unit} · 第 ${page}/${pageCount} 页` : ''}
      </span>

      {multi ? (
        <div className="pager__nav">
          {onPageSize ? (
            <select
              className="select pager__select"
              value={pageSize}
              onChange={(e) => onPageSize(Number(e.target.value))}
              aria-label="每页条数"
            >
              {pageSizes.map((n) => (
                <option key={n} value={n}>
                  每页 {n} {unit}
                </option>
              ))}
            </select>
          ) : null}

          <button
            type="button"
            className="pager__btn"
            disabled={page <= 1}
            onClick={() => onPage(page - 1)}
            aria-label="上一页"
          >
            <Icon name="chevronLeft" size={14} />
          </button>

          {pageWindow(page, pageCount).map((n, i) =>
            n === '…' ? (
              <span key={`gap-${i}`} className="pager__gap">
                …
              </span>
            ) : (
              <button
                key={n}
                type="button"
                className="pager__btn"
                data-active={n === page}
                aria-current={n === page ? 'page' : undefined}
                onClick={() => onPage(n)}
              >
                {n}
              </button>
            ),
          )}

          <button
            type="button"
            className="pager__btn"
            disabled={page >= pageCount}
            onClick={() => onPage(page + 1)}
            aria-label="下一页"
          >
            <Icon name="chevronRight" size={14} />
          </button>
        </div>
      ) : null}
    </div>
  )
}

/**
 * 「列表 + 分页」的一体化封装 —— 列表类 UI 的唯一入口。
 *
 * 用法：
 * ```tsx
 * <PagedList items={jobs} resetKey={filter} unit="条"
 *   render={(j) => <JobRow key={j.id} job={j} />} />
 * ```
 *
 * ⚠️ 调用方**不要**自己再 `slice()`：分页由这里统一负责。
 * 同一份列表在多处各自截断，就是「本页历史有的页面 4 条、有的页面全量」那类漂移的成因。
 */
export function PagedList<T>({
  items,
  render,
  pageSize = 10,
  pageSizes,
  resetKey,
  className = 'job-list',
  unit = '条',
  empty,
}: {
  items: T[]
  render: (item: T, index: number) => ReactNode
  pageSize?: number
  pageSizes?: number[]
  /** 筛选条件 / 页签键；变化时自动回到第 1 页 */
  resetKey?: unknown
  /** 外层容器类名，默认 `job-list`（纵向列表）；网格传 `work-grid` / `result-grid` */
  className?: string
  unit?: string
  empty?: ReactNode
}) {
  const p = usePagination(items, { pageSize, resetKey })

  if (!items.length) return <>{empty ?? null}</>

  return (
    <>
      <div className={className}>
        {p.pageItems.map((item, i) => render(item, i))}
      </div>
      <Pager
        page={p.page}
        pageCount={p.pageCount}
        total={p.total}
        from={p.from}
        to={p.to}
        pageSize={p.pageSize}
        onPage={p.setPage}
        onPageSize={p.setPageSize}
        pageSizes={pageSizes}
        unit={unit}
      />
    </>
  )
}
